import LoadingBackdrop from 'components/LoadingBackdrop'
import React, { useEffect, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useQueryClient } from '@tanstack/react-query'
import {
  Column, HeaderGroup, usePagination, useTable,
} from 'react-table'
import { useSticky } from 'react-table-sticky'
import TableHeader from 'routes/Transits/Table/TableHeader'
import { TablePagination } from 'routes/Transits/Table/TablePagination'
import { TableFilter } from 'types/PageResponse'
import reportApiFailure from 'shared/api/reportApiFailure'
import getTraderTableHeaders from '../config'
import useTraderPageQuery from '../api/useTraderPageQuery'
import { deleteTrader, useUpdateTraderMutation } from '../api/traderMutations'
import { traderQueryKeys } from '../keys'
import { TraderRequest, TraderResponse } from '../models/types'
import TraderEditPanel from './TraderEditPanel'
import TraderTableRow from './TraderTableRow'

const DEFAULT_PAGE_SIZE = 50
const FILTER_DEBOUNCE_MS = 300

function TraderTable() {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<Array<TableFilter>>([])
  const [editing, setEditing] = useState<TraderResponse | null>(null)

  const columns = useMemo(() => getTraderTableHeaders(t), [t])
  const nameFilter = filters.find((filter) => filter.key === 'name')?.value ?? ''
  const [debouncedName, setDebouncedName] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedName(nameFilter), FILTER_DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [nameFilter])

  const [totalPages, setTotalPages] = useState(0)

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns: columns as unknown as Column<object>[],
      data: [],
      initialState: { pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE },
      manualPagination: true,
      pageCount: totalPages,
    },
    useSticky,
    usePagination,
  )

  const { data, isFetching, isError } = useTraderPageQuery(debouncedName, pageIndex, pageSize)

  useEffect(() => {
    setTotalPages(data?.totalPages ?? 0)
  }, [data?.totalPages])
  const queryClient = useQueryClient()
  const updateTrader = useUpdateTraderMutation()

  useEffect(() => {
    gotoPage(0)
  }, [debouncedName, gotoPage])

  useEffect(() => {
    if (!isFetching && data?.content.length === 0 && pageIndex > 0) {
      gotoPage(Math.max(0, (data.totalPages ?? 1) - 1))
    }
  }, [data, isFetching, pageIndex, gotoPage])

  const notifyActionFailed = () => toast.error(t('traders.actionFailed'))

  useEffect(() => {
    if (isError) {
      notifyActionFailed()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError])

  const onSave = (request: TraderRequest) => {
    if (!editing) {
      return
    }

    updateTrader.mutate(
      { id: editing.id, request },
      {
        onSuccess: () => setEditing(null),
        onError: (error) => {
          reportApiFailure(error, notifyActionFailed)
        },
      },
    )
  }

  const onDelete = (trader: TraderResponse) => {
    // eslint-disable-next-line no-alert
    if (window.confirm(t('traders.confirmDelete', { name: trader.name ?? trader.identificationNumber }))) {
      deleteTrader(trader.id)
        .then(() => queryClient.invalidateQueries({ queryKey: traderQueryKeys.all }))
        .catch((error) => {
          reportApiFailure(error, notifyActionFailed)
        })
    }
  }

  return (
    <>
      <div className={`table__container table-responsive trader-table__container ${isMobile ? 'not-in-mobile' : ''}`}>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <table {...getTableProps()} className="table sticky table-hover fixed-columns mb-0">
          <TableHeader
            headerGroups={headerGroups as unknown as HeaderGroup[]}
            filterState={{ filters, setFilters }}
          />
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <tbody {...getTableBodyProps()} className="position-relative">
            {data?.content.length === 0 && !isFetching && (
              <tr>
                <td colSpan={columns.length}>{t('traders.empty')}</td>
              </tr>
            )}
            {data?.content.map((trader) => (
              <TraderTableRow
                key={trader.id}
                trader={trader}
                onEdit={() => setEditing(trader)}
                onDelete={() => onDelete(trader)}
              />
            ))}
          </tbody>
          <LoadingBackdrop loading={isFetching} />
        </table>
      </div>
      <TablePagination
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        pageOptions={pageOptions}
        pageCount={pageCount}
        gotoPage={gotoPage}
        nextPage={nextPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
        pageIndex={pageIndex}
        pageSize={pageSize}
      />
      <TraderEditPanel
        trader={editing}
        isSaving={updateTrader.isLoading}
        onCancel={() => setEditing(null)}
        onSave={onSave}
      />
    </>
  )
}

export default TraderTable
